import { IndentValues } from "@/lib/utils/IndentValues";
import { Box, Indent, TextProps, Txt } from "@stenajs-webui/core";
import { Tooltip } from "@stenajs-webui/tooltip";
import { useCallback, useRef, useState } from "react";

interface Props {
  value: string | null | undefined;
  variant?: TextProps["variant"];
}

export const EllipsisCell: React.FC<Props> = ({ value, variant }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  /**
   * A tooltip repeating text that is already fully visible is just noise, so it
   * only opens when the text is actually cut off. Measured on hover rather than
   * observed: a table renders one of these per cell, and measuring then is both
   * cheap and always current, whatever the column has grown to.
   */
  const showWhenTruncated = useCallback(() => {
    const element = textRef.current;
    setTooltipVisible(
      element != null && element.scrollWidth > element.clientWidth
    );
  }, []);

  const hide = useCallback(() => setTooltipVisible(false), []);

  return (
    <Box
      width={"calc(100% - 8px)"}
      overflow={"hidden"}
      onMouseEnter={showWhenTruncated}
      onMouseLeave={hide}
    >
      {/*
        Aligned to the start rather than centred: the tooltip's reference is the
        full width of the cell, so centring puts the bubble in the middle of the
        column while the text it describes sits flush left.
      */}
      <Tooltip
        label={value ?? ""}
        visible={tooltipVisible}
        placement={"top-start"}
        appendTo={document.body}
      >
        <Indent num={IndentValues.EIGHT}>
          <Txt
            ref={textRef}
            variant={variant}
            overflow={"hidden"}
            textOverflow={"ellipsis"}
            whiteSpace={"nowrap"}
          >
            {value}
          </Txt>
        </Indent>
      </Tooltip>
    </Box>
  );
};
